import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Questionnaire } from '@/components/Questionnaire';
import { useState, useCallback, useEffect } from 'react';
import { useGTM } from '@/hooks/useGTM';
import { ProgressiveLoading } from '@/components/ProgressiveLoading';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load components
const Calculator = lazy(() => import('@/components/Calculator').then(m => ({ default: m.Calculator })));
const TrustElements = lazy(() => import('@/components/TrustElements').then(m => ({ default: m.TrustElements })));
const ApplicationSuccess = lazy(() => import('@/components/ApplicationSuccess').then(m => ({ default: m.ApplicationSuccess })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="animate-pulse space-y-8">
    <div className="h-64 bg-gray-200 rounded-lg"></div>
  </div>
);

export function App() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  // Initialize GTM tracking
  useGTM();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    // Check if we have all required parameters to show the result page
    const hasRequiredParams = ['age', 'gender', 'coverage', 'firstName', 'phoneNumber']
      .every(param => searchParams.has(param));

    // Only redirect to result if we're on the home page
    if (hasRequiredParams && isInitialLoad && currentPath === '/') {
      navigate(`/result?${searchParams.toString()}`);
    }
    
    setIsInitialLoad(false);
  }, [searchParams, navigate, isInitialLoad, currentPath]);

  const handleQuestionnaireComplete = useCallback((data: {
    age: string;
    gender: string;
    coverage: string;
    firstName: string;
    phoneNumber: string;
    motivations?: string[];
    beneficiary?: string;
    coverageStart?: string;
    health?: string;
  }) => {
    const params = new URLSearchParams({
      age: data.age,
      gender: data.gender,
      coverage: data.coverage,
      firstName: data.firstName,
      phoneNumber: data.phoneNumber,
    });
    
    // Add new fields to URL parameters
    if (data.motivations && data.motivations.length > 0) {
      params.set('motivations', data.motivations.join(','));
    }
    if (data.beneficiary) {
      params.set('beneficiary', data.beneficiary);
    }
    if (data.coverageStart) {
      params.set('coverageStart', data.coverageStart);
    }
    if (data.health) {
      params.set('health', data.health);
    }
    
    window.history.replaceState({}, '', `/?${params.toString()}`);
    setShowLoading(true);
  }, []);

  const handleLoadingComplete = () => {
    navigate(`/result${window.location.search}`);
  };


  return (
    <div className="min-h-screen bg-[#f9f3f1]">
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <header className="text-center space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1"></div>
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-gray-600">{t("Powered by")}</span>
                <img 
                  src="https://cdn.prod.website-files.com/59dbe1c25428050001926167/65fd8d37fa9bead34a98121e_logo-emma-circle-white.svg" 
                  alt="Emma Insurance" 
                  className="h-[32px] sm:h-[40px] w-auto"
                />
              </div>
              <div className="flex-1 flex justify-end">
                <LanguageToggle />
              </div>
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#494d62] tracking-tight">
              {t("Final Expense Insurance Calculator")}
            </h1>
            <p className="text-base sm:text-lg text-[#494d62]">
              {t("Compare rates from top insurers in Canada.")}
            </p>
          </header>
          
          <Routes>
            <Route path="/" element={
              showLoading ? (
                <ProgressiveLoading 
                  firstName={searchParams.get('firstName') || ''} 
                  onComplete={handleLoadingComplete}
                />
              ) : (
                <Questionnaire onComplete={handleQuestionnaireComplete} />
              )
            } />
            <Route path="/result" element={
              <Suspense fallback={<LoadingFallback />}>
                <Calculator 
                  initialFirstName={searchParams.get('firstName') || ''}
                  {...{
                    initialAge: searchParams.get('age') || '',
                    initialGender: searchParams.get('gender') || '',
                    initialCoverage: searchParams.get('coverage') || '',
                    initialFirstName: searchParams.get('firstName') || '',
                    initialPhoneNumber: searchParams.get('phoneNumber') || ''
                  }}
                />
              </Suspense>
            } />
            <Route path="/apply" element={
              <Suspense fallback={<LoadingFallback />}>
                <ApplicationSuccess firstName={searchParams.get('firstName') || ''} />
              </Suspense>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <Suspense fallback={<LoadingFallback />}>
            <TrustElements />
          </Suspense>
        </div>
      </main>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;